/**
 * Export a scope as ManaBox-compatible CSV.
 *
 * The API answers `{ filename, csv }` as JSON so the session cookie travels
 * with the request; the download itself is a Blob URL minted here, which is
 * what makes it work in the Capacitor webview as well as a browser tab.
 */

import { useState } from 'react';
import { ApiError, type Api } from '../api/http.js';
import { Banner } from './Banner.js';

export interface ExportScope {
  scope: 'collection' | 'container' | 'bin';
  id?: string;
  label: string;
}

export function ExportButton({ api, target }: { api: Api; target: ExportScope }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const download = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const result = await api.exportCsv({ scope: target.scope, ...(target.id ? { id: target.id } : {}) });
      const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      try {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = result.filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        // Revoke on the next tick so the download has started (Safari needs it).
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      setDone(
        result.count === 0
          ? 'Nothing to export yet: file a card first.'
          : `Downloaded ${result.count} card${result.count === 1 ? '' : 's'}.`,
      );
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'the export could not be built');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="export">
      <button
        type="button"
        className="ghost"
        disabled={busy}
        aria-label={`Export ${target.label} as CSV`}
        onClick={() => void download()}
      >
        {busy ? 'Building…' : `Export ${target.label}`}
      </button>
      {error && <Banner kind="error">{error}</Banner>}
      {done && !error && <span className="hint" role="status">{done}</span>}
    </span>
  );
}
